import {Component, OnDestroy, OnInit} from '@angular/core';
import {ITaxReturn} from "../model/i-tax-return";
import {Subscription} from "rxjs";
import {TaxReturnsResolver} from "../../tax-returns-resolver";
import {
  IAnswer,
  IAnswerWarning,
  IFormField
} from "../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import {IAnswerFlag, IGroup, ISubGroup} from "../subcategory/subcategory";
import {AnswerService, CommentService, TaxReturnService} from "../../../../../swaggerServices";
import moment from "moment";
import {IComment} from "../subcategory/comments/comments.component";
import {UserInfoService} from "../../../user-info.service";
import { cloneDeep } from "lodash-es";
import {handleUrlsForNewComment} from "../../get-full-tax-return";
import {Category, Subcategory} from "../../../../../swaggerServices";
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { AttachmentsService, Folders } from '../subcategory/comments/attachments/attachments.service';
import { CommentValue } from "../subcategory/comments/attachments/attachments.component";

interface IQuestionWithTaxStructureMeta extends IFormField {
  category?: Category,
  subcategory?: Subcategory,
  group?: IGroup,
  subgroup?: ISubGroup
}

@Component({
    selector: 'app-missing-information',
    templateUrl: './missing-information.component.html',
    styleUrls: ['./missing-information.component.scss'],
    providers: [
        {
            provide: TRANSLOCO_SCOPE,
            useValue: { scope: 'tax-returns', alias: 'taxReturns' },
        }
    ],
    standalone: false
})
export class MissingInformationComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  taxReturn?: ITaxReturn;
  taxReturnWithMissingInfo?: ITaxReturn;
  question: IQuestionWithTaxStructureMeta;

  folders: Folders = {
    rootFolder: "",
    attachmentsFolder: "",
    targetFolder: "",
  };

  constructor(
    private taxReturnsResolver: TaxReturnsResolver,
    private userInfo: UserInfoService,
    private taxReturnService: TaxReturnService,
    private answerService: AnswerService,
    private commentService: CommentService,
    private attachmentsService: AttachmentsService,
  ) { }

  ngOnInit() {
    this.subscriptions.push(this.taxReturnsResolver.getTaxReturnObs().subscribe((data: ITaxReturn) =>
      this.initMissingInfo(cloneDeep(data))));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  initMissingInfo(taxReturn: ITaxReturn) {
    this.taxReturn = taxReturn;
    let taxReturnWithMissingInfo = taxReturn;
    this.attachmentsService.setRootFolder(this.folders, this.taxReturn.docshareRootFolder);

    taxReturnWithMissingInfo.categories.forEach(c => {
      c.isCollapsed = false;
      c.subcategories.forEach(sc => {
        sc.isCollapsed = false;
        sc.groups.forEach(g => {
          g.isCollapsed = false;
          g.subgroups.forEach(sg => {
            sg.isCollapsed = false;

            // filter question which contain 'MI' flags or warnings
            sg.questions = sg.questions.filter(q =>
              q.answers.some(a => {
                return a.answerFlags.filter(aF => aF.status === 0 && aF.flagId === 2).length > 0
                  || a.answerWarnings.filter(aW => aW.status === 0 && aW.warningId === 2).length > 0
              }));

            // append ui meta
            sg.questions.forEach(q => {
              q.answers = q.answers.map(a => {
                a.answerFlags = a.answerFlags.filter(aF => aF.flagId === 2);
                a = this.initFlag(a, q);
                a.answerWarnings = a.answerWarnings.filter(aW => aW.warningId === 2);
                a = this.initAnswerWarnings(a, q.question);
                return a;
              });
            });

          });
        })
      });
    });

    // only show groups which contain flag
    taxReturnWithMissingInfo.categories = taxReturnWithMissingInfo.categories.filter(c => {
      c.subcategories = c.subcategories.filter(sc => {
        sc.groups = sc.groups.filter(g => {
          g.subgroups = g.subgroups.filter(sg => sg.questions.length > 0);
          return g.subgroups.length > 0;
        });
        return sc.groups.length > 0;
      });
      return c.subcategories.length > 0;
    });

    this.taxReturnWithMissingInfo = taxReturnWithMissingInfo;

    // select first question
    const firstCat = this.taxReturnWithMissingInfo.categories[0];
    if (firstCat) {
      const firstSubcat = firstCat.subcategories[0];
      if (firstSubcat) {
        const firstGroup = firstSubcat.groups[0];
        if (firstGroup) {
          const firstSubgroup = firstGroup.subgroups[0];
          if (firstSubgroup) {
            const firstQuestion = firstSubgroup.questions[0];
            if (firstQuestion)
              this.selectQuestion(firstQuestion, firstCat, firstSubcat, firstGroup, firstSubgroup);
          }
        }
      }
    }

  }

  selectQuestion(question: IFormField, category: Category, subcategory: Subcategory, group: IGroup, subgroup: ISubGroup) {
    this.question = {
      ...question,
      category: category,
      subcategory: subcategory,
      group: group,
      subgroup: subgroup
    };
  }

  initFlag(a: IAnswer, q: IFormField, group?: IGroup, subgroup?: ISubGroup, rowIndex?: number) {

    if (!a.answerFlags || a.answerFlags.length === 0) return a;

    a.answerFlags = a.answerFlags.map(aF => {

      const answerFlag: any = {
        ...aF,
        answer: q.answers.find(a => a.id === aF.answerId),
        question: q,
        groupId: group ? group.id : null,
        subGroupId: subgroup ? subgroup.id : null,
        groupName: group ? (group.deleted ? group.deletedName : group.name) : null,
        subGroupName: subgroup ? subgroup.name : null,
        rowIndex: rowIndex,
        createdAtString: moment(aF.createdAt).format("MMMM Do YYYY, h:mm:ss a")
      };

      if (a.comments && a.comments.length > 0) {
        const flagComment = a.comments.find(c => c.answerFlagId === aF.id && !c.parentCommentId);
        if (flagComment) {
          answerFlag.parentComment = this.getCommentMeta(flagComment);
          // find replies to parent comment
          answerFlag.parentComment.comments = [];
          a.comments.forEach(cc => {
            if (cc.parentCommentId === answerFlag.parentComment.id)
              answerFlag.parentComment.comments.push(this.getCommentMeta(cc));
          });
        }
      }

      answerFlag.replyTo = false;
      return answerFlag;

    });
    return a;
  };

  getCommentMeta(c: IComment) {
    let user;
    let role;
    if (c.commentAudits && c.commentAudits.length && this.taxReturn && this.taxReturn.preparer && this.taxReturn.preparer.find(p => p.id === c.commentAudits[0].userId)) {
      user = this.taxReturn.preparer.find(p => p.id === c.commentAudits[0].userId);
      role = 'Preparer';
    } else if (c.commentAudits && c.commentAudits.length && this.taxReturn && this.taxReturn.reviewer && this.taxReturn.reviewer.find(p => p.id === c.commentAudits[0].userId)) {
      user = this.taxReturn.reviewer.find(p => p.id === c.commentAudits[0].userId);
      role = 'Reviewer';
    } else {
      user = ( c.commentAudits && c.commentAudits.length) ? c.commentAudits[0]['user'] : null;
      role = '';
    }

    const author = user ? user.firstName + ' ' + user.lastName : (c.autoDocLink ? 'Auto generated' : 'Unknown User');
    role = role ? role : (c.autoDocLink ? 'System' : 'Unknown role');

    const m = moment(c.createdAt);
    return {
      id: c.id,
      message: c.message,
      parts: c.parts,
      author,
      role,
      date: m.format("MMMM Do YYYY, h:mm:ss a"),
      dateMoment: m,
      isReply: Boolean(c.parentCommentId)
    };

  };

  readonly() {
    return !this.taxReturn
      || this.taxReturn.snapshotId != null
      || !this.taxReturn.assignedUser
      || this.taxReturn.assignedUser.id !== this.userInfo.id
      || this.taxReturn.assignee.status === 99
      || this.taxReturn.fullStatus.readOnly;
  }

  // submit a comment reply
  async replyToFlag(answerFlag: IAnswerFlag, closeFlag?: boolean) {
    const { attachments, success } = await this.attachmentsService.uploadAttachments(this.folders, this.question.subcategory.name, Array.from(answerFlag.replyComment.attachments.values()));
    if (!success) return;

    const _comment: any = {
      parentCommentId: answerFlag.parentComment.id,
      message: answerFlag.replyComment.message,
      attachments,
      answerIds: [answerFlag.answerId]
    };

    this.commentService.addComment(_comment).subscribe((response: any) => {
      let newComment = response.comment;
      if ( newComment && newComment.commentAudits && newComment.commentAudits.length ) {
        newComment.commentAudits[0].user = response.user;
      }
      handleUrlsForNewComment(this.taxReturnService, this.taxReturn, newComment).subscribe(() => {
      });

      // append reply to parent comment
      answerFlag.parentComment.comments.push(this.getCommentMeta(newComment));

      if (closeFlag) {
        this.answerService.updateAnswerFlag({
          answerFlagIds: [answerFlag.id],
          status: 99
        }).subscribe(() => {
          answerFlag.replyTo = false;
          answerFlag.replyComment = { message: "", attachments: new Map() };
          answerFlag.status = 99;
        });
      } else {
        answerFlag.replyTo = false;
        answerFlag.replyComment = { message: "", attachments: new Map() };
      }
    });
  }

  initAnswerWarnings(a: IAnswer, q: string) {
    if (!a.answerWarnings || a.answerWarnings.length === 0) return a;

    a.answerWarnings = a.answerWarnings.map(answerWarning => {

      answerWarning = {
        ...answerWarning,
        answer: a,
        question: q
      };

      if (!answerWarning.createdAtString)
        answerWarning.createdAtString = moment(answerWarning.createdAt).format("MMMM Do YYYY, h:mm:ss a");

      // find comments on the warning
      answerWarning.replies = [];
      a.comments.forEach(c => {
        if (c.answerWarningId === answerWarning.id) {
          answerWarning.replies.push(this.getCommentMeta(c));
        }
      });

      answerWarning.replyTo = false;
      answerWarning.replyComment = { message: "", attachments: new Map() };

      return answerWarning;

    });
    return a;
  };

  cancelReply(comment: IComment) {
    comment.replyTo = false;
    comment.replyComment = { message: "", attachments: new Map() };
  }

  async saveAnsWarningReply(answerWarning: IAnswerWarning, comment: CommentValue) {
    const { attachments, success } = await this.attachmentsService.uploadAttachments(this.folders, this.question.subcategory.name, Array.from(comment.attachments.values()));
    if (!success) return;

    const _comment: any = {
      message: comment.message,
      attachments,
      answerIds: [answerWarning.answerId],
      answerWarningId: answerWarning.id,
    };

    this.commentService.addComment(_comment).subscribe(
      (response: any) => {
        let newComment = response.comment;
        if ( newComment && newComment.commentAudits && newComment.commentAudits.length ) {
          newComment.commentAudits[0].user = response.user;
        }
        handleUrlsForNewComment(this.taxReturnService, this.taxReturn, newComment).subscribe(() => {
        });
        answerWarning.replyTo = false;
        answerWarning.replyComment = { message: "", attachments: new Map() };
        answerWarning.answer.comments.push(newComment);
        answerWarning.status = 1; // cause the count to update immediately
        setTimeout(() => {
          this.initAnswerWarnings(answerWarning.answer, answerWarning.question);
        });
      });
  }

  // checks whether a question has any remaining active MI flags/warnings
  hasClosedAll(question) {
    return question.answers.every(a =>
      a.answerFlags.every(aF => aF.status !== 0) && a.answerWarnings.every(aW => aW.status !== 0))
  }


}
